import { applyPatch } from 'fast-json-patch';
import type { Operation, ReplaceOperation } from 'fast-json-patch';
import { produce } from 'immer';

import type { CasinoRacesWidgetState } from 'widgets/casino-races-widget/model';
import type { CasinoGame, GameLike, StreamData } from '@swf/core/types/CasinoGame';
import { isGame } from '@swf/core/utils/casino';

export const setLoading = <S>(state: S, payload: boolean) => ({
  ...state,
  loading: payload
});

export const set = <S>(state: S, payload: Partial<S>) => ({ ...state, ...payload });

export const favorites = <T extends StreamData<GameLike>>(
  state: T,
  { body }: { body: StreamData<CasinoGame> }
) =>
  produce(state, (draft) => {
    body.games.forEach(({ gametype, variant }) =>
      draft.games.filter(isGame).forEach((game) => {
        game.isFavorite = game.gametype === gametype && game.variant === variant;
      })
    );
  });

export interface FSBUpdate {
  gId: number;
  vId: number;
}

export const freeSpinBonus = <T extends StreamData<GameLike>>(
  state: T,
  { body }: { body: FSBUpdate[] }
) =>
  produce(state, (draft) => {
    draft.games.filter(isGame).forEach((game: CasinoGame) => {
      game.fsb = false;
      body.forEach(({ gId, vId }) => {
        if (game.gametype === gId && game.variant === vId) {
          game.fsb = true;
        }
      });
    });
  });

export const applyRaceUpdate = (
  state: CasinoRacesWidgetState,
  { body, id }: { id: string; body: Operation[] }
) =>
  produce(state, (draft) => {
    if (body) {
      const known = draft.races && draft.races[draft.knownRaces[id]];
      if (known) {
        applyPatch(known, body);
      }
    }
    return draft;
  });

export const raceStandingUpdate = (
  state: CasinoRacesWidgetState,
  { body }: { body: ReplaceOperation<{ raceId: string }>[]; id: string }
) =>
  produce(state, (draft) => {
    body.forEach((patch) => {
      const { raceId } = patch.value;
      const known = draft.races && draft?.races[draft.knownRaces[raceId]];
      if (known) {
        applyPatch(known, [patch]);
      }
    });
    return draft;
  });
